import { Form, Select, Tag } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { apiApi } from '../../../../../apis'
import { ActionEnum, AlertMessageEnum } from '../../../../../common'
import {
  ManageModalComponent,
  ManageModalFooterComponent,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { ApiEntity, ApiMethodEnum, UserTypeEnum } from '../../../../../models'
import { RootState } from '../../../../../stores'
import {
  loadProfile,
  loadUserTypes,
  setAction,
  setActionProcessing,
  setAlert,
  setApis,
} from '../../../../../stores/actions'

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
  display: flex;
`

type InputApi = {
  accessibleUserTypes: number[]
}

export const ManageApiModalComponent: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { management } = useSelector((state: RootState) => state.common)
  const { userTypes, isUserTypesLoaded } = useSelector(
    (state: RootState) => state.user
  )
  const { apis, selectedApi } = useSelector((state: RootState) => state.api)
  const readOnlyMode = React.useMemo(
    () => (management.action === ActionEnum.VIEW ? true : false),
    [management]
  )

  React.useEffect(() => {
    if (!isUserTypesLoaded) {
      dispatch(loadUserTypes())
    }
    // eslint-disable-next-line
  }, [])

  const replaceApi = (api: ApiEntity) => {
    const index = apis.map((item) => item.id).indexOf(api.id)
    if (index > -1) {
      dispatch(
        setApis([...apis.slice(0, index), api, ...apis.slice(index + 1)])
      )
    }
  }

  const onFinish = async (value: InputApi) => {
    dispatch(setAlert())
    dispatch(setActionProcessing(true))
    switch (management.action) {
      case ActionEnum.VIEW:
        dispatch(setAction(ActionEnum.UPDATE))
        break
      case ActionEnum.UPDATE:
        if (selectedApi) {
          try {
            const resp = await apiApi.updateApi(selectedApi.id, value)
            replaceApi(resp.data)
            dispatch(
              setAlert({
                key: 'api.update.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e && e.response && e.response.status === 401) {
              dispatch(loadProfile())
              const resp = await apiApi.updateApi(selectedApi.id, value)
              replaceApi(resp.data)
              dispatch(
                setAlert({
                  key: 'api.update.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key: 'api.update.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
    }
    dispatch(setActionProcessing(false))
  }

  const body = selectedApi && (
    <Form onFinish={onFinish}>
      {(!management.alertMessage ||
        management.alertMessage.type !== AlertMessageEnum.SUCCESS) && (
        <>
          <StyledFormItem
            label={
              <span style={{ fontWeight: 'bold' }}>
                {translate('api.name', language)}
              </span>
            }
          >
            {translate(
              `api.method.${selectedApi.method.toLowerCase()}.detail`,
              language
            ) +
              ' ' +
              translate(
                `api.name.${selectedApi.path.replace(/\//, '')}`,
                language
              )}
          </StyledFormItem>
          <StyledFormItem
            label={
              <span style={{ fontWeight: 'bold' }}>
                {translate('api.method', language)}
              </span>
            }
          >
            <Tag
              style={{ fontSize: '0.7rem' }}
              color={
                selectedApi.method === ApiMethodEnum.GET
                  ? 'processing'
                  : selectedApi.method === ApiMethodEnum.POST
                  ? 'success'
                  : selectedApi.method === ApiMethodEnum.PUT
                  ? 'warning'
                  : 'error'
              }
              key={selectedApi.method}
            >
              {translate(
                `api.method.${(selectedApi.method || '').toLowerCase()}`,
                language
              )}
            </Tag>
          </StyledFormItem>
          <StyledFormItem
            label={
              <span style={{ fontWeight: 'bold' }}>
                {translate('api.path', language)}
              </span>
            }
          >
            <Tag style={{ fontSize: '0.7rem' }} color={'default'}>
              {selectedApi.path}
            </Tag>
          </StyledFormItem>
          <StyledFormItem
            label={
              <span style={{ fontWeight: 'bold' }}>
                {translate('api.accessibleUserTypes', language)}
              </span>
            }
            name="accessibleUserTypes"
            initialValue={
              selectedApi
                ? selectedApi.accessibleUserTypes.map((item) => item.id)
                : undefined
            }
          >
            {readOnlyMode ? (
              <>
                {selectedApi.accessibleUserTypes.map((item) => (
                  <Tag
                    key={item.id}
                    color={item.type === UserTypeEnum.CUSTOMER ? 'blue' : 'red'}
                  >
                    {translate(
                      `user.userType.${item.type.toLowerCase()}`,
                      language
                    )}
                  </Tag>
                ))}
              </>
            ) : (
              <Select
                mode="multiple"
                maxTagCount="responsive"
                style={{
                  flexGrow: 1,
                }}
                disabled={readOnlyMode}
              >
                {userTypes
                  .filter((item) => item.type !== UserTypeEnum.ADMIN)
                  .map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {translate(
                        `user.userType.${item.type.toLowerCase()}`,
                        language
                      )}
                    </Select.Option>
                  ))}
              </Select>
            )}
          </StyledFormItem>
        </>
      )}
      <ManageModalFooterComponent hasDefaultButtons />
    </Form>
  )

  return <ManageModalComponent body={body} />
}
